"use client";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { values as data } from "@/lib/data/careers";

const accentColors = ["var(--color-crimson)", "var(--color-purple)", "var(--color-cyan)", "var(--color-emerald)", "var(--color-amber)"];

export function Values() {
  return (
    <Section id="our-values" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-10 glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgb(var(--border)/0.18)] bg-[rgb(var(--border)/0.04)]">
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Value</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">What It Means</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => {
                    const accent = accentColors[i % accentColors.length]!;
                    return (
                      <tr key={row.value} className="border-b border-[rgb(var(--border)/0.08)]">
                        <td className="px-4 py-3 align-top">
                          <span className="eyebrow-tiny font-bold" style={{ color: accent }}>{String(i + 1).padStart(2, "0")}</span>{" "}
                          <span className="font-semibold">{row.value}</span>
                        </td>
                        <td className="px-4 py-3 align-top text-[rgb(var(--fg-muted))]">{row.meaning}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>
        <div className="mt-10 grid lg:grid-cols-2 gap-5">
          {data.rows.map((row, i) => {
            const accent = accentColors[i % accentColors.length]!;
            return (
              <Reveal key={`detail-${row.value}`} delay={i * 80}>
                <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)` }}>
                  <span className="eyebrow-tiny font-bold" style={{ color: accent }}>{row.value}</span>
                  <p className="mt-3 text-sm font-semibold uppercase tracking-wider" style={{ color: accent }}>{row.meaning}</p>
                  <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{row.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={500}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
